import { useRFSTrendingCount } from "@/api/rfs";
import { H4 } from "@/components/ui/typography";
import { useEffect, useRef, useState } from "react";
import { axisBottom, axisLeft, scaleBand, scaleLinear, select } from "d3";

export default function RFSTrendingSubmission({
  selectedPICLinknets,
  selectedPICVendors,
  selectedTenantNames,
  selectedVendorNames,
  selectedDateMonthYear,
  isVendor,
  companyCode,
}: {
  selectedPICLinknets: string;
  selectedPICVendors: string;
  selectedTenantNames: string;
  selectedVendorNames: string;
  selectedDateMonthYear: string;
  isVendor: boolean;
  companyCode?: string;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [currentWidth, setCurrentWidth] = useState<number>();

  const { data: rfsTrendingCount } = useRFSTrendingCount({
    picLinknet: selectedPICLinknets,
    picVendor: selectedPICVendors,
    tenantName: selectedTenantNames,
    vendorName: isVendor ? companyCode : selectedVendorNames,
    dateMonthYear: selectedDateMonthYear,
  });

  const height = 500;
  const marginTop = 30;
  const marginRight = 0;
  const marginBottom = 30;
  const marginLeft = 40;

  useEffect(() => {
    if (!svgRef.current) {
      return;
    }
    svgRef.current.innerHTML = "";
    setCurrentWidth(svgRef.current.clientWidth ?? 0);
  }, []);

  useEffect(() => {
    function handleResize() {
      setCurrentWidth(svgRef.current?.clientWidth ?? 0);
    }
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (
      !svgRef.current ||
      !tooltipRef.current ||
      !rfsTrendingCount ||
      !currentWidth
    ) {
      return;
    }

    const svg = select(svgRef.current);
    const tooltip = select(tooltipRef.current);

    const x = scaleBand()
      .domain(Object.keys(rfsTrendingCount))
      .range([marginLeft, currentWidth - marginRight])
      .padding(0.3);

    const y = scaleLinear()
      .domain([0, Math.max(...Object.values(rfsTrendingCount)) + 1])
      .range([height - marginBottom, marginTop]);

    svg
      .append("g")
      .selectAll()
      .data(Object.entries(rfsTrendingCount))
      .join("rect")
      .attr("fill", "#f7941e")
      .attr("x", ([key]) => x(key) as number)
      .attr("y", ([_, value]) => y(value) as number)
      .attr(
        "height",
        ([_, value]) => (height - marginBottom - y(value)) as number,
      )
      .attr("width", x.bandwidth())
      .on("mouseover", function (event, [key, value]) {
        tooltip.transition().duration(200).style("opacity", 0.9);
        tooltip
          .style("left", `${event.pageX}px`)
          .style("top", `${event.pageY - 60}px`)
          .html(
            `<div style="background-color: white; padding: 15px; border-radius: 5px; border: solid 2px black">${key}: ${value}</div>`,
          );
      })
      .on("mousemove", function (event) {
        tooltip
          .style("left", `${event.pageX}px`)
          .style("top", `${event.pageY - 60}px`);
      })
      .on("mouseout", function () {
        tooltip.transition().duration(200).style("opacity", 0);
      })
      .each(function ([key, value]) {
        svg
          .append("text")
          .attr("x", x.bandwidth() / 2 + (key ? (x(key) as number) : 0))
          .attr("y", y(value) - 5)
          .attr("text-anchor", "middle")
          .text(value);
      });

    svg
      .append("g")
      .attr("transform", `translate(0, ${height - marginBottom})`)
      .call(axisBottom(x));

    svg
      .append("g")
      .attr("transform", `translate(${marginLeft}, 0)`)
      .call(axisLeft(y))
      .call((g) => g.select(".domain").remove())
      .call((g) =>
        g
          .append("text")
          .attr("x", -marginLeft)
          .attr("y", 10)
          .attr("fill", "currentColor")
          .attr("text-anchor", "start")
          .text("Count"),
      );

    return () => {
      svg.selectAll("*").remove();
    };
  }, [currentWidth, rfsTrendingCount]);

  return (
    <>
      <H4>Trending Issue RFS Submission</H4>
      <svg
        ref={svgRef}
        height={height}
        width={currentWidth}
        className="h-[530px] w-full rounded  p-4 dark:bg-neutral-50"
        data-testid="rfs-trending-chart"
      />
      <div ref={tooltipRef} className="absolute opacity-0" />
    </>
  );
}
